import { createContext, useContext, type ReactNode } from 'react';
import { LOCALE_COOKIE } from './negotiate';

/**
 * Public-site locales (same identifiers as @colescomex/platform SUPPORTED_LOCALES).
 * Explicit routes: Spanish under /es/ (intake /iniciar-proyecto/), English under /en/. The root / is
 * the x-default negotiator (see ./negotiate.ts). Content is authored per locale in typed dictionaries.
 */
export type Locale = 'es-CO' | 'en';
export const LOCALES: readonly Locale[] = ['es-CO', 'en'];
export const DEFAULT_LOCALE: Locale = 'es-CO';
export type PageId = 'home' | 'intake';

/** Public routes per page and locale (deep-linkable, prerendered, indexable). */
export const ROUTES: Record<PageId, Record<Locale, string>> = {
  home: { 'es-CO': '/es/', en: '/en/' },
  intake: { 'es-CO': '/iniciar-proyecto/', en: '/en/start-project/' },
};
export const HTML_LANG: Record<Locale, string> = { 'es-CO': 'es-CO', en: 'en' };
export const OG_LOCALE: Record<Locale, string> = { 'es-CO': 'es_CO', en: 'en_US' };
/** Root negotiator (x-default). */
export const ROOT_PATH = '/';
/** Stored only when the visitor explicitly switches language (cookie for the edge, localStorage mirror). */
export const LOCALE_KEY = LOCALE_COOKIE;

export function localeFromLang(lang: string | null | undefined): Locale { return lang?.toLowerCase().startsWith('en') ? 'en' : 'es-CO'; }

const LocaleContext = createContext<{ locale: Locale; page: PageId }>({ locale: DEFAULT_LOCALE, page: 'home' });
export function LocaleProvider({ locale, page, children }: { locale: Locale; page: PageId; children: ReactNode }) {
  return <LocaleContext.Provider value={{ locale, page }}>{children}</LocaleContext.Provider>;
}
export function useLocale() { return useContext(LocaleContext); }

/** Explicit choice only (the ES | EN switch): a first-party preference cookie the edge can read, one year. */
export function rememberLocale(locale: Locale) {
  try { document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(locale)}; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`; } catch { /* cookies unavailable */ }
  try { localStorage.setItem(LOCALE_KEY, locale); } catch { /* storage unavailable */ }
}
